import { Injectable } from '@angular/core';
import mixpanel from 'mixpanel-browser';

import { environment } from '../../../../environments/environment';
import { StageNameEnum } from '../../consumer/dip/enum/stage-name.enum';
import { MixpanelEventPropertiesEnum } from './enum/mixpanel-event-properties.enum';
import { MixpanelEventsEnum } from './enum/mixpanel-events.enum';
import { MixpanelUserProfilePropertiesEnum } from './enum/mixpanel-user-profile-properties.enum';
import { RegisterModel } from '../../models/auth/register.model';
import { StorageKeyEnum } from '../storage/enum/storage-key.enum';
import { StorageService } from '../storage/storage.service';
import { isProduction } from '../../utils/environments';
import { AuthService } from '../auth/auth.service';

/**
 * Service to manage MixPanel tracking operations.
 */
@Injectable({
  providedIn: 'root'
})
export class MixpanelService {
  constructor(private readonly storage: StorageService, private readonly authService: AuthService) {
    if (environment.canExecuteTrackers) {
      MixpanelService.init();
    }
    this.storage.saveValue(StorageKeyEnum.MIXPANEL_EMAIL, null);
  }

  /**
   * Register the Super Properties that will be added to every tracked event.
   */
  static registerSuperProperties(
    email: string | undefined,
    fcaNumber: string | undefined,
    brokerageName?: string | undefined
  ): void {
    if (environment.canExecuteTrackers) {
      mixpanel.register({
        [MixpanelUserProfilePropertiesEnum.EMAIL]: email,
        [MixpanelUserProfilePropertiesEnum.FCA_NUMBER]: fcaNumber
      });
      if (brokerageName) {
        mixpanel.register({
          [MixpanelUserProfilePropertiesEnum.BROKERAGE_NAME]: brokerageName
        });
      }
    }
  }

  /**
   * Tracks the Sign-Up event. Creates a Mixpanel User with the register data.
   */
  trackSignUpEvent(registerData: RegisterModel): void {
    if (environment.canExecuteTrackers) {
      MixpanelService.registerSuperProperties(registerData.email, registerData.fcaNumber);
      mixpanel.track(MixpanelEventsEnum.SIGN_UP);
      mixpanel.identify(registerData.email);
      mixpanel.people.set({
        [MixpanelUserProfilePropertiesEnum.FIRST_NAME]: registerData.firstName,
        [MixpanelUserProfilePropertiesEnum.LAST_NAME]: registerData.lastName,
        [MixpanelUserProfilePropertiesEnum.PHONE_NUMBER]: registerData.phoneNumber,
        [MixpanelUserProfilePropertiesEnum.EMAIL]: registerData.email,
        [MixpanelUserProfilePropertiesEnum.FCA_NUMBER]: registerData.fcaNumber,
        [MixpanelUserProfilePropertiesEnum.CREATED_DATE]: new Date().toISOString()
      });
    }
  }

  /**
   * Tracks the Mixpanel login event.
   */
  trackLoginEvent(registerData: RegisterModel): void {
    if (environment.canExecuteTrackers) {
      this.storage.saveValue(StorageKeyEnum.MIXPANEL_EMAIL, registerData.email);
      mixpanel.track(MixpanelEventsEnum.LOGIN, {
        broker_account_type: this.getBrokerAccountType()
      });
      mixpanel.identify(this.storage.getValue(StorageKeyEnum.MIXPANEL_EMAIL));
      mixpanel.people.set({
        [MixpanelUserProfilePropertiesEnum.FIRST_NAME]: registerData.firstName,
        [MixpanelUserProfilePropertiesEnum.LAST_NAME]: registerData.lastName,
        [MixpanelUserProfilePropertiesEnum.PHONE_NUMBER]: registerData.phoneNumber,
        [MixpanelUserProfilePropertiesEnum.EMAIL]: registerData.email,
        [MixpanelUserProfilePropertiesEnum.FCA_NUMBER]: registerData.fcaNumber,
        [MixpanelUserProfilePropertiesEnum.LAST_TIME_LOGIN]: new Date().toISOString()
      });
      mixpanel.people.increment(MixpanelUserProfilePropertiesEnum.LIFETIME_LOGIN);
    }
  }

  /**
   * Tracks the Mixpanel Start Consumer event.
   */
  trackStartConsumerEvent(): void {
    if (environment.canExecuteTrackers) {
      mixpanel.track(MixpanelEventsEnum.START_CONSUMER, {
        broker_account_type: this.getBrokerAccountType()
      });
      mixpanel.people.set({ [MixpanelUserProfilePropertiesEnum.LAST_START_CONSUMER]: new Date().toISOString() });
      mixpanel.people.set_once(MixpanelUserProfilePropertiesEnum.FIRST_START_CONSUMER, new Date());
      mixpanel.people.increment(MixpanelUserProfilePropertiesEnum.LIFETIME_START_CONSUMER);
    }
  }

  /**
   * Tracks a Mixpanel event with the opportunity reference.
   */
  trackEventWithReference(event: MixpanelEventsEnum, reference?: string): void {
    if (environment.canExecuteTrackers) {
      mixpanel.track(event, { [MixpanelEventPropertiesEnum.REFERENCE_NUMBER]: reference });
    }
  }

  /**
   * Tracks the Mixpanel Error Page event. Triggered when an error page is shown to a broker.
   */
  trackErrorPageEvent(page: StageNameEnum, reference: string): void {
    MixpanelService.trackEvent(MixpanelEventsEnum.ERROR_PAGE, page, reference);
  }

  /**
   * Tracks the Mixpanel Error Message event. Triggered when an error messages appears below a CTA.
   */
  trackErrorMessageEvent(page: StageNameEnum, reference: string): void {
    MixpanelService.trackEvent(MixpanelEventsEnum.ERROR_MESSAGE, page, reference);
  }

  /**
   * Tracks when the Hometrack valuation toggle is clicked in Broker Portal.
   */
  trackBpHometrackToggleClickedEvent(
    id: string,
    quoteData: {
      toggleFrom: string;
      toggleTo: string;
    }
  ): void {
    MixpanelService.trackEventWithData(MixpanelEventsEnum.BP_HOMETRACK_TOGGLE_CLICKED, {
      case_id: id,
      toggle_from: quoteData.toggleFrom,
      toggle_to: quoteData.toggleTo
    });
  }

  /**
   * Tracks when offers are rendered in Broker Portal.
   */
  trackBpOffersRenderedEvent(
    id: string,
    quoteData: {
      valuationMode: string;
      caseHasHometrackIneligibleOffer: boolean;
      hometrackIneligibilityReasons: string[];
    }
  ): void {
    MixpanelService.trackEventWithData(MixpanelEventsEnum.BP_OFFERS_RENDERED, {
      case_id: id,
      valuation_mode: quoteData.valuationMode,
      case_has_hometrack_ineligible_offer: quoteData.caseHasHometrackIneligibleOffer,
      hometrack_ineligibility_reasons: quoteData.hometrackIneligibilityReasons
    });
  }

  /**
   * Tracks when the Quick Quote page is loaded
   */
  trackQuickQuotePageLoadedEvent(externalApplicationId: string): void {
    if (environment.canExecuteTrackers) {
      const eventData: Record<string, unknown> = {
        external_application_id: externalApplicationId,
        broker_account_type: this.getBrokerAccountType(),
        timestamp: new Date().toISOString()
      };
      MixpanelService.trackEventWithData(MixpanelEventsEnum.QUICK_QUOTE_PAGE_LOADED, eventData);
    }
  }

  /**
   * Tracks when the broker clicks Generate quick quote
   */
  trackQuickQuoteGenerateClickedEvent(
    externalApplicationId: string,
    quoteData: {
      loanPurpose: string;
      loanAmount: number;
      term: number;
      estimatedValue: number;
      outstandingBalance: number;
      ltv: number;
      addressPostcode: string;
      numberOfApplicants: number;
      qq_applicant1_employment_status: string;
      qq_applicant2_employment_status?: string;
      hasAdditionalIncomeSource1: boolean;
      hasAdditionalIncomeSource2: boolean;
    }
  ): void {
    if (environment.canExecuteTrackers) {
      const eventData: Record<string, unknown> = {
        external_application_id: externalApplicationId,
        qq_loan_purpose: quoteData.loanPurpose,
        qq_loan_amount: quoteData.loanAmount,
        qq_term: quoteData.term,
        qq_estimated_value: quoteData.estimatedValue,
        qq_outstanding_balance: quoteData.outstandingBalance,
        qq_ltv: quoteData.ltv,
        qq_address_postcode: quoteData.addressPostcode,
        qq_number_of_applicants: quoteData.numberOfApplicants,
        qq_applicant1_employment_status: quoteData.qq_applicant1_employment_status,
        qq_applicant2_employment_status: quoteData.qq_applicant2_employment_status,
        applicant1_additional_income_source: quoteData.hasAdditionalIncomeSource1,
        applicant2_additional_income_source: quoteData.hasAdditionalIncomeSource2,
        broker_account_type: this.getBrokerAccountType(),
        timestamp: new Date().toISOString()
      };
      MixpanelService.trackEventWithData(MixpanelEventsEnum.QUICK_QUOTE_GENERATE_CLICKED, eventData);
    }
  }

  /**
   * Tracks when select quick quote offer
   */
  trackQuickQuoteGeneratedEvent(
    externalApplicationId: string,
    caseId: string,
    quoteData: {
      loanPurpose: string;
      loanAmount: number;
      term: number;
      estimatedValue: number;
      outstandingBalance: number;
      ltv: number;
      addressPostcode: string;
      numberOfApplicants: number;
      qq_applicant1_employment_status: string;
      qq_applicant2_employment_status?: string;
      hasAdditionalIncomeSource1: boolean;
      hasAdditionalIncomeSource2: boolean;
      decision_result: string;
    }
  ): void {
    if (environment.canExecuteTrackers) {
      const eventData: Record<string, unknown> = {
        external_application_id: externalApplicationId,
        case_id: caseId,
        qq_loan_purpose: quoteData.loanPurpose,
        qq_loan_amount: quoteData.loanAmount,
        qq_term: quoteData.term,
        qq_estimated_value: quoteData.estimatedValue,
        qq_outstanding_balance: quoteData.outstandingBalance,
        qq_ltv: quoteData.ltv,
        qq_address_postcode: quoteData.addressPostcode,
        qq_number_of_applicants: quoteData.numberOfApplicants,
        qq_applicant1_employment_status: quoteData.qq_applicant1_employment_status,
        qq_applicant2_employment_status: quoteData.qq_applicant2_employment_status,
        applicant1_additional_income_source: quoteData.hasAdditionalIncomeSource1,
        applicant2_additional_income_source: quoteData.hasAdditionalIncomeSource2,
        decision_result: quoteData.decision_result,
        broker_account_type: this.getBrokerAccountType(),
        timestamp: new Date().toISOString()
      };
      MixpanelService.trackEventWithData(MixpanelEventsEnum.QUICK_QUOTE_GENERATED, eventData);
    }
  }

  /**
   * Tracks when the broker updates a quick quote
   */
  trackQuickQuoteUpdatedEvent(
    externalApplicationId: string,
    updatedData: {
      loanPurpose?: string;
      loanAmount?: number;
      term?: number;
      estimatedValue?: number;
      outstandingBalance?: number;
      ltv?: number;
      addressPostcode?: string;
      numberOfApplicants?: number;
      qq_applicant1_employment_status?: string;
      qq_applicant2_employment_status?: string;
      qq_applicant1_total_income?: number;
      qq_applicant2_total_income?: number;
      hasAdditionalIncomeSource1?: boolean;
      hasAdditionalIncomeSource2?: boolean;
      decision_result?: string;
      caseId?: string;
    }
  ): void {
    if (environment.canExecuteTrackers) {
      const changedProperties: Record<string, unknown> = {
        external_application_id: externalApplicationId,
        case_id: updatedData.caseId,
        qq_loan_purpose: updatedData.loanPurpose,
        qq_loan_amount: updatedData.loanAmount,
        qq_term: updatedData.term,
        qq_estimated_value: updatedData.estimatedValue,
        qq_outstanding_balance: updatedData.outstandingBalance,
        qq_ltv: updatedData.ltv,
        qq_current_address_postcode: updatedData.addressPostcode,
        qq_number_of_applicants: updatedData.numberOfApplicants,
        qq_applicant1_employment_status: updatedData.qq_applicant1_employment_status,
        qq_applicant2_employment_status: updatedData.qq_applicant2_employment_status,
        qq_applicant1_total_income: updatedData.qq_applicant1_total_income,
        qq_applicant2_total_income: updatedData.qq_applicant2_total_income,
        applicant1_additional_income_source: updatedData.hasAdditionalIncomeSource1,
        applicant2_additional_income_source: updatedData.hasAdditionalIncomeSource2,
        decision_result: updatedData.decision_result,
        broker_account_type: this.getBrokerAccountType(),
        timestamp: new Date().toISOString()
      };

      MixpanelService.trackEventWithData(MixpanelEventsEnum.QUICK_QUOTE_UPDATED, changedProperties);
    }
  }

  /**
   * Tracks when the broker clicks Continue application to proceed to DIP
   */
  trackQuickQuoteContinueApplicationEvent(
    externalApplicationId: string,
    caseId: string,
    selectedOffer: {
      selectedOfferId: string;
      affordabilitySurplus: number;
      debtToIncomeRatio: number;
      cltv: number;
      loanToIncomeRatio: number;
      decision: 'accept' | 'decline';
    }
  ): void {
    if (environment.canExecuteTrackers) {
      MixpanelService.trackEventWithData(MixpanelEventsEnum.QUICK_QUOTE_CONTINUE_APPLICATION_CLICKED, {
        external_application_id: externalApplicationId,
        case_id: caseId,
        qq_final_product_selected: selectedOffer.selectedOfferId,
        qq_affordability_surplus: selectedOffer.affordabilitySurplus,
        qq_debt_to_income_ratio: selectedOffer.debtToIncomeRatio,
        qq_cltv: selectedOffer.cltv,
        qq_loan_to_income_ratio: selectedOffer.loanToIncomeRatio,
        qq_decision: selectedOffer.decision,
        broker_account_type: this.getBrokerAccountType(),
        timestamp: new Date().toISOString()
      });
    }
  }

  /**
   * Tracks DIP events with case ID and timestamp.
   */
  trackDipFacilityDetailsLoadingEvent(reference: string): void {
    const externalApplicationId = this.storage.getValue(StorageKeyEnum.GENERATED_EXTERNAL_APPLICATION_ID);
    if (environment.canExecuteTrackers) {
      MixpanelService.trackEventWithData(MixpanelEventsEnum.DIP_FACILITY_DETAILS, {
        external_application_id: externalApplicationId,
        case_id: reference,
        broker_account_type: this.getBrokerAccountType(),
        timestamp: new Date().toISOString()
      });
    }
  }

  private static init(): void {
    mixpanel.init(environment.mixpanel.token, {
      api_host: 'https://api-eu.mixpanel.com',
      debug: !isProduction(),
      track_pageview: 'url-with-path',
      record_mask_text_selector: '',
      record_mask_all_inputs: false,
      record_sessions_percent: 100
    });
  }

  private static trackEvent(event: MixpanelEventsEnum, page: StageNameEnum, reference: string): void {
    if (environment.canExecuteTrackers && page && reference) {
      mixpanel.track(event, {
        [MixpanelEventPropertiesEnum.PAGE]: page,
        [MixpanelEventPropertiesEnum.REFERENCE_NUMBER]: reference
      });
    }
  }

  private static trackEventWithData(event: MixpanelEventsEnum, data: Record<string, unknown>): void {
    if (environment.canExecuteTrackers) {
      mixpanel.track(event, data);
    }
  }

  /**
   * Gets the broker account type based on isAdvicePlus flag
   */
  private getBrokerAccountType(): string | undefined {
    return this.authService.isAdvicePlus ? 'Advice+' : undefined;
  }
}
